import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package2, X, ZoomIn, PlayCircle } from 'lucide-react';
import { FullScreenImage } from './FullScreenImage';

// Definir el tipo para los items del carrusel
interface CarouselItem {
  type: 'image' | 'video';
  url: string;
}

interface ProductImageCarouselProps {
  items: CarouselItem[];
  productName: string;
  onClose: () => void;
}

export function ProductImageCarousel({
  items,
  productName,
  onClose,
}: ProductImageCarouselProps) {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [showFullScreen, setShowFullScreen] = useState(false);

  // Filtrar items válidos
  const validItems = useMemo(() => {
    return items.filter(item => item && item.url);
  }, [items]);

  // Resetear el índice cuando cambian los items
  useEffect(() => {
    setCurrentItemIndex(0);
  }, [validItems.length]);

  // Funciones de navegación
  const goToNextItem = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (validItems.length <= 1) return;
    setCurrentItemIndex((prevIndex) => (prevIndex + 1) % validItems.length);
  }, [validItems.length]);

  const goToPrevItem = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (validItems.length <= 1) return;
    setCurrentItemIndex((prevIndex) => (prevIndex - 1 + validItems.length) % validItems.length);
  }, [validItems.length]);

  // Item actual
  const currentItem = validItems[currentItemIndex];

  // Manejo de errores específico para este componente si es necesario
  const [mediaError, setMediaError] = useState(false);
  useEffect(() => {
    setMediaError(false); // Resetear error al cambiar de item
  }, [currentItemIndex]);

  const handleMediaError = () => {
    console.error("Error cargando media:", currentItem?.url);
    setMediaError(true);
  };

  return (
    <>
      <div className="relative w-full bg-zinc-900/80">
        {/* Contenido principal (Imagen o Video) */}
        <div className="relative w-full group cursor-pointer overflow-hidden">
          {currentItem ? (
            <div className="aspect-auto md:aspect-auto lg:aspect-auto min-h-[50vh] lg:min-h-[70vh] overflow-hidden relative bg-black flex items-center justify-center">
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={`item-${currentItemIndex}`}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="w-full h-full"
                >
                  {mediaError ? (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                      <div className="text-center">
                        <X className="w-12 h-12 text-red-500 mx-auto mb-2" />
                        <p className="text-zinc-400 text-sm">Error al cargar</p>
                      </div>
                    </div>
                  ) : currentItem.type === 'video' ? (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      {/* Log para depuración */}
                      {(() => {
                        console.log("Renderizando video con URL:", currentItem.url);
                        return null;
                      })()}

                      <video
                        key={`video-${currentItem.url}`}
                        src={currentItem.url}
                        controls
                        className="w-full h-full object-contain"
                        preload="metadata"
                        playsInline
                        onError={(e) => {
                          console.error("Error cargando video:", e);
                          handleMediaError();
                        }}
                        autoPlay={false}
                      >
                        Tu navegador no soporta la reproducción de videos.
                      </video>

                      <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                        <div className="px-3 py-1 bg-[#FCAA0D]/90 text-black text-sm font-semibold rounded-full">
                          Video del producto
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={currentItem.url}
                      alt={`${productName} - Media ${currentItemIndex + 1}`}
                      className="w-full h-full object-contain transition-transform duration-300"
                      onError={handleMediaError}
                      loading="eager"
                    />
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Overlay para zoom (solo en imágenes) */}
              {currentItem.type === 'image' && !mediaError && (
                 <div
                  className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                  onClick={() => setShowFullScreen(true)}
                 >
                   <div className="transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/30 p-3 rounded-full backdrop-blur-sm">
                     <ZoomIn className="w-6 h-6 text-white" />
                   </div>
                 </div>
              )}
            </div>
          ) : (
            <div className="aspect-[4/3] md:aspect-[16/10] lg:aspect-[16/9] bg-zinc-800 flex items-center justify-center">
              <div className="text-center">
                <Package2 className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">No hay media disponible</p>
              </div>
            </div>
          )}

          {/* Contador de items */}
          {validItems.length > 1 && (
            <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm text-white text-sm font-medium border border-white/10">
              {currentItemIndex + 1} / {validItems.length}
            </div>
          )}
        </div>

        {/* Miniaturas */}
        {validItems.length > 1 && (
          <div className="p-3 bg-black/20">
            <div className="flex gap-2 overflow-x-auto pb-2 snap-x scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
              {validItems.map((item, index) => (
                <motion.button
                  key={`thumb-${index}`}
                  className={`flex-shrink-0 snap-start relative rounded-md overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] transition-all duration-200 hover:shadow-md ${
                    currentItemIndex === index ? 'ring-2 ring-[#FCAA0D] shadow-[0_0_0_2px_rgba(252,170,13,0.3)]' : 'opacity-70 hover:opacity-100'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentItemIndex(index);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-black relative flex items-center justify-center">
                    {item.type === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center bg-[#FCAA0D]/10">
                        <PlayCircle className="w-8 h-8 text-[#FCAA0D]" />
                      </div>
                    ) : (
                      <img
                        src={item.url}
                        alt={`${productName} - Miniatura ${index + 1}`}
                        className="w-full h-full object-contain p-1"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        loading="lazy"
                      />
                    )}
                    {item.type === 'video' && (
                       <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                         <PlayCircle className="w-6 h-6 text-white" />
                       </div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modo pantalla completa (solo para imágenes) */}
      <AnimatePresence>
        {showFullScreen && currentItem?.type === 'image' && (
          <FullScreenImage
            imageUrl={currentItem.url}
            alt={`${productName} - Imagen ${currentItemIndex + 1}`}
            onClose={() => setShowFullScreen(false)}
            onNext={goToNextItem}
            onPrevious={goToPrevItem}
            hasMultipleImages={validItems.filter(i => i.type === 'image').length > 1}
          />
        )}
      </AnimatePresence>
    </>
  );
}
